import type { UseQueryOptions } from '@tanstack/react-query';

import { ROVER_SEARCH_SOURCE_TYPE_UNKNOWN } from '@rover/constants/src/search/search.constants';
import makeSearchQuery from '@rover/react-lib/src/pages/landing/utilities/makeSearchQuery';
import type { ApiSearchOptimizedRetrieveParams } from '@rover/rsdk/src/apiClient/latest/model/apiSearchOptimizedRetrieveParams';
import { EngineEnum } from '@rover/rsdk/src/apiClient/latest/model/engineEnum';
import type { LocalizedBrandService } from '@rover/rsdk/src/apiClient/latest/model/localizedBrandService';
import type { OptimizedSearchResponse } from '@rover/rsdk/src/apiClient/latest/model/optimizedSearchResponse';
import { isServiceLanding, ServiceLandingEnum } from '@rover/types/src/ServiceLanding';
import { filterNullishProperties } from '@rover/utilities/object';

import { SERVICE_LANDINGS_WITH_SEARCH_RESULTS } from '../constants';

// The 5 types the legacy engine already fetches search results for in production. Kept separate
// from SERVICE_LANDINGS_WITH_SEARCH_RESULTS (11 types) so widening the template's carousel
// doesn't also turn on new, unreviewed search-fetch/prefill behavior on live legacy pages that
// never had it. Delete this split and go back to one list once the "rollout_service_landing_template"
// Statsig dynamic config (page_engine) fully rolls out and the legacy engine is retired.
const LEGACY_SERVICE_LANDINGS_WITH_SEARCH_RESULTS: ServiceLandingEnum[] = [
  ServiceLandingEnum.DOG_BOARDING,
  ServiceLandingEnum.HOUSE_SITTING,
  ServiceLandingEnum.PET_SITTING,
  ServiceLandingEnum.DOGGY_DAY_CARE,
  ServiceLandingEnum.DOG_WALKING,
];

// Client-only by design: this page has no location in its URL, so a server-side fetch would
// resolve location from the visitor and leak into the page's public edge cache.
export const getServiceLandingSearchQueryOptions = (
  serviceLandingData: LocalizedBrandService | undefined,
  engine: EngineEnum
): UseQueryOptions<OptimizedSearchResponse> => {
  const serviceLandingType: ServiceLandingEnum | null =
    serviceLandingData && isServiceLanding(serviceLandingData.serviceLandingUrlSlug)
      ? serviceLandingData.serviceLandingUrlSlug
      : null;

  const searchResultsGate =
    engine === EngineEnum.template
      ? SERVICE_LANDINGS_WITH_SEARCH_RESULTS
      : LEGACY_SERVICE_LANDINGS_WITH_SEARCH_RESULTS;

  const shouldFetchSearchResults =
    serviceLandingType !== null && searchResultsGate.includes(serviceLandingType);

  // serviceLandingData.searchParams is backend-computed (LocalizedServiceKeyword.search_params)
  // and already carries the canonical serviceType plus pet counts/puppy flag derived from this
  // page's real pet type — reconstructing those fields here duplicated, and dropped, that data.
  const searchOptimizedRetrieveParams: ApiSearchOptimizedRetrieveParams = filterNullishProperties(
    serviceLandingData?.searchParams ?? {}
  );

  return makeSearchQuery(searchOptimizedRetrieveParams, {
    enabled: shouldFetchSearchResults,
    searchSourceType: ROVER_SEARCH_SOURCE_TYPE_UNKNOWN,
  });
};
